import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Clock, Mail, Instagram } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useSEO, breadcrumb, faqPage } from "@/lib/useSEO";
import { useStudio } from "@/context/StudioContext";
import { createContact } from "@/lib/api";
import { trackContactSubmitted } from "@/lib/analytics";

const STUDIO_ADDRESS = "184 Victoria Road, Drummoyne NSW 2047";
const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(
  `My Floor Studio, ${STUDIO_ADDRESS}`
)}&z=16&output=embed`;
const GOOGLE_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  STUDIO_ADDRESS
)}`;
const APPLE_DIRECTIONS = `https://maps.apple.com/?daddr=${encodeURIComponent(STUDIO_ADDRESS)}`;

const STUDIO_PLACE = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://myfloorstudio.com.au/#studio",
  name: "My Floor Studio",
  url: "https://myfloorstudio.com.au/",
  telephone: "+61272467899",
  email: "info@myfloorstudio.com.au",
  image: "https://myfloorstudio.com.au/logo/logo-primary.png",
  priceRange: "$$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "184 Victoria Road",
    addressLocality: "Drummoyne",
    addressRegion: "NSW",
    postalCode: "2047",
    addressCountry: "AU",
  },
  geo: { "@type": "GeoCoordinates", latitude: -33.8548, longitude: 151.1547 },
  hasMap: GOOGLE_DIRECTIONS,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Saturday"], opens: "09:00", closes: "17:00" },
  ],
};

const CONTACT_FAQ = faqPage([
  {
    q: "How do I book an appointment at the Drummoyne studio?",
    a: "Click 'Book Appointment' anywhere on the site to request a 45-minute private consultation with a senior studio member. You'll receive a confirmation email once we lock in the time.",
  },
  {
    q: "Do you offer trade pricing for architects and interior designers?",
    a: "Yes — we run a dedicated trade programme with volume pricing, sample boards for client presentations, and lead-time-aware specification support. Contact the studio to be added to the trade portal.",
  },
  {
    q: "What are your opening hours?",
    a: "The studio at 184 Victoria Road, Drummoyne is open Monday to Saturday, 9:00–17:00. Private after-hours consultations can be arranged on request.",
  },
  {
    q: "Where is My Floor Studio and how do I get there?",
    a: "The studio is at 184 Victoria Road, Drummoyne NSW 2047 — on Victoria Road between Drummoyne and Five Dock, minutes from the Iron Cove Bridge. Street parking directly in front of the studio is available between 10am and 3pm, as Victoria Road is a clearway outside those hours; the adjoining side streets are unrestricted at any time. The Victoria Road bus corridor stops at the door. Visits are by appointment.",
  },
]);

export default function Contact() {
  useSEO({
    title:
      "Contact · Visit My Floor Studio, 184 Victoria Road Drummoyne Sydney",
    description:
      "Visit the Drummoyne studio, send an enquiry, or request a private consultation. 184 Victoria Road, Drummoyne NSW 2047. Open Mon–Sat 9–5. Timber, Carpet, Tile and Stone under one roof.",
    path: "/contact",
    keywords:
      "contact my floor studio, Drummoyne showroom, 184 Victoria Road Drummoyne, flooring showroom Sydney, book flooring consultation, luxury flooring enquiry Sydney",
    imageAlt:
      "The My Floor Studio showroom at 184 Victoria Road, Drummoyne — Sydney",
    jsonLd: [
      STUDIO_PLACE,
      CONTACT_FAQ,
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
    ],
  });
  const { openBooking } = useStudio();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await createContact(form);
      trackContactSubmitted(form);
      toast.success("Note received", { description: "We'll be in touch shortly." });
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />

      <main className="pt-40 md:pt-48 pb-24">
        <section className="mx-auto max-w-[1600px] px-6 md:px-12">
          <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">Contact</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl xl:text-8xl leading-[0.96] max-w-4xl"
          >
            Visit the studio,<br />or send us a note
          </motion.h1>
        </section>

        <section className="mx-auto max-w-[1600px] px-6 md:px-12 mt-24 md:mt-32 grid lg:grid-cols-12 gap-16">
          {/* Details */}
          <div className="lg:col-span-5">
            <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-6">Details</p>
            <div className="space-y-6">
              {[
                { icon: MapPin, label: "Studio", val: "184 Victoria Road, Drummoyne NSW 2047" },
                { icon: Clock, label: "Hours", val: "Monday - Friday: 9:00am - 5:00pm\nSaturday: By Appointment\nSunday: Closed" },
                { icon: Mail, label: "Enquiries", val: "info@myfloorstudio.com.au", href: "mailto:info@myfloorstudio.com.au" },
                { icon: Instagram, label: "Instagram", val: "@myfloorstudio_au", href: "https://www.instagram.com/myfloorstudio_au/", external: true },
              ].map((r) => (
                <div key={r.label} className="flex items-start gap-5 border-t border-[#8C7355]/18 pt-6">
                  <r.icon size={18} className="text-[#C9A876] mt-1 shrink-0" strokeWidth={1.4} />
                  <div>
                    <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">{r.label}</p>
                    {r.href ? (
                      <a
                        href={r.href}
                        data-testid={`contact-${r.label.toLowerCase()}-link`}
                        {...(r.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="text-[#F5F4F0] text-base md:text-lg font-body mt-1 link-underline"
                      >
                        {r.val}
                      </a>
                    ) : (
                      <p className="text-[#F5F4F0] text-base md:text-lg font-body mt-1 whitespace-pre-line">{r.val}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12">
              <button
                data-testid="contact-book-btn"
                onClick={openBooking}
                className="btn-bronze inline-flex items-center rounded-full px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase font-medium"
              >
                Request a private consultation
              </button>
              <p className="mt-4 text-[#D6D1CA]/50 text-[11px] tracking-[0.2em] uppercase">
                One hour · one senior curator
              </p>
            </div>
          </div>

          {/* Enquiry form */}
          <div className="lg:col-span-7">
            <div className="bg-[#1a1b1e]/60 backdrop-blur-md border border-[#8C7355]/20 p-8 md:p-12">
              <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-3">Send us a note</p>
              <h2 className="font-serif-display text-[#F5F4F0] text-3xl md:text-4xl font-light tracking-tight">
                Tell us about your project
              </h2>
              <p className="mt-4 text-[#D6D1CA]/60 font-body text-sm">
                We reply in one business day. For a same-week consultation, use Request Appointment instead.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-7" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <input data-testid="contact-name" className="field-underline" placeholder="Full name" value={form.name} onChange={set("name")} />
                  <input data-testid="contact-email" type="email" className="field-underline" placeholder="Email address" value={form.email} onChange={set("email")} />
                </div>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  className="field-underline resize-none w-full"
                  placeholder="Your project brief — location, timing, elements you have in mind"
                  value={form.message}
                  onChange={set("message")}
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-bronze rounded-full px-10 py-3.5 text-[11px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send Enquiry"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Map & directions */}
      <section className="border-t border-[#8C7355]/15" data-testid="contact-map-section">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20 md:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-6">Getting here</p>
            <h2 className="font-serif-display text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight">
              184 Victoria Road,<br />Drummoyne
            </h2>
            <div className="mt-8 space-y-5 text-[#D6D1CA]/75 font-body text-sm leading-relaxed">
              <p>
                On Victoria Road between Drummoyne and Five Dock, a few minutes from the Iron Cove
                Bridge and an easy run from Balmain, Rozelle, Concord and the Lower North Shore.
              </p>
              <div className="border-t border-[#8C7355]/18 pt-5">
                <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Parking</p>
                <p className="mt-1.5">
                  Street parking directly in front of the studio is available between 10am and 3pm
                  — Victoria Road is a clearway outside those hours. Unrestricted parking is
                  available on the adjoining side streets at any time.
                </p>
              </div>
              <div className="border-t border-[#8C7355]/18 pt-5">
                <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Public transport</p>
                <p className="mt-1.5">
                  Victoria Road bus corridor (routes to the City and Parramatta) stops at the door;
                  Drummoyne Wharf is a short drive for the Parramatta River ferry.
                </p>
              </div>
              <div className="border-t border-[#8C7355]/18 pt-5">
                <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Visits</p>
                <p className="mt-1.5">By appointment, Monday to Friday 9–5 and Saturday on request.</p>
              </div>
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={GOOGLE_DIRECTIONS}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-google"
                className="btn-bronze inline-flex items-center rounded-full px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-medium"
              >
                Google Maps directions
              </a>
              <a
                href={APPLE_DIRECTIONS}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-apple"
                className="inline-flex items-center rounded-full border border-[#D6D1CA]/30 px-6 py-3 text-[11px] tracking-[0.2em] uppercase text-[#D6D1CA] hover:text-[#F5F4F0] hover:border-[#C9A876]"
                style={{ transition: "color .4s ease, border-color .4s ease" }}
              >
                Apple Maps
              </a>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="relative border border-[#8C7355]/25 overflow-hidden h-[380px] md:h-[520px]">
              <iframe
                title="Map of My Floor Studio, 184 Victoria Road, Drummoyne NSW 2047"
                data-testid="contact-map-embed"
                src={MAP_EMBED}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0, filter: "grayscale(0.35) contrast(1.05) brightness(0.95)" }}
                allowFullScreen
              />
            </div>
            <p className="mt-4 text-[#D6D1CA]/45 text-[10px] tracking-[0.28em] uppercase">
              My Floor Studio · 184 Victoria Road, Drummoyne NSW 2047 · 02 7246 7899
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
